import { Box, Chip, Stack } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { getVoyageShipmentHistory } from '../../../../services/VoyageRepository';
import MarketLabelChip from '../shared/MarketLabelChip';

interface ShipmentHistoryTableProps {
  selectedShipmentRow?: any;
}

const ShipmentHistoryTable = ({
  selectedShipmentRow,
}: ShipmentHistoryTableProps) => {
  const [shipmentHistoryData, setShipmentHistoryData] = useState<any[]>([]);
  const [isShipmentHistoryLoading, setIsShipmentHistoryLoading] =
    useState(false);

  useEffect(() => {
    const fetchShipmentHistory = async () => {
      if (!selectedShipmentRow.shipmentId) return;
      try {
        setIsShipmentHistoryLoading(true);
        const data = await getVoyageShipmentHistory(
          selectedShipmentRow.shipmentId
        );
        setShipmentHistoryData(data);
      } catch (error) {
        console.error('Error fetching voyage history shipments:', error);
      } finally {
        setIsShipmentHistoryLoading(false);
      }
    };
    fetchShipmentHistory();
  }, [selectedShipmentRow.shipmentId]);

  const shipmentHistoryColumns: GridColDef[] = [
    {
      field: 'version',
      headerName: 'Version',
      width: 130,
      type: 'number',
      renderCell: (params) => {
        const isBiggestVersion =
          Math.max(...shipmentHistoryData.map((v) => v.version)) ===
          params.value;
        return (
          <Stack
            direction='row'
            alignItems='center'
            height={'100%'}
            justifyContent={'end'}
            spacing={1}
          >
            {isBiggestVersion && (
              <Chip
                variant='outlined'
                color='warning'
                label='Previous'
                size='small'
              />
            )}
            <Chip variant='outlined' label={`v${params.value}`} size='small' />
          </Stack>
        );
      },
      align: 'center',
      headerAlign: 'center',
      description:
        'Version number of the shipment history (latest highlighted)',
      sortable: true,
    },
    {
      field: 'market',
      headerName: 'Market',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      description: 'Market associated with this shipment',
      renderCell: (params) => (
        <MarketLabelChip
          label={`${params.row.market.id} (${params.row.market.name})`}
        />
      ),
    },
    {
      field: 'firstShippingLineOffer',
      headerName: 'First Shipping Line Offer',
      width: 160,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      description: 'Initial offer from shipping line',
    },
    {
      field: 'adjustedPoRequest',
      headerName: 'Adjusted PO Request',
      width: 160,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      description: 'Adjusted purchase order request',
    },
    {
      field: 'finalAgreement',
      headerName: 'Final Agreement',
      width: 140,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      description: 'Final agreement value',
    },
    {
      field: 'effectiveShipped',
      headerName: 'Effective Shipped',
      width: 140,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      description: 'Actual shipped quantity',
    },
    {
      field: 'changed',
      headerName: 'Changed',
      width: 120,
      type: 'boolean',
      align: 'center',
      headerAlign: 'center',
      description: 'Indicates if this version was changed',
      renderCell: (params) =>
        params.value ? (
          <Chip
            label='Yes'
            color='warning'
            size='small'
            sx={{
              minWidth: 50,
            }}
          />
        ) : (
          <Chip
            label='No'
            color='default'
            size='small'
            sx={{
              minWidth: 50,
            }}
          />
        ),
    },
    {
      field: 'lastUpdatedBy',
      headerName: 'Edited By',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      description: 'User who last edited this version',
      renderCell: (params) => (
        <Chip
          label={params.value?.emea ?? ''}
          color='primary'
          size='small'
          variant='outlined'
          sx={{ fontWeight: 'bold', minWidth: 100 }}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      description: 'Date of last update',
      renderCell: (params) => (
        <Box>{new Date(params.value).toLocaleDateString()}</Box>
      ),
    },
  ];

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minHeight: 400 }}>
      <DataGridPro
        loading={isShipmentHistoryLoading}
        columns={shipmentHistoryColumns}
        rows={shipmentHistoryData}
        disableColumnMenu={false}
        pageSizeOptions={[5, 10]}
        initialState={{
          sorting: { sortModel: [{ field: 'version', sort: 'desc' }] },
          pinnedColumns: { left: ['version'] },
        }}
        slots={{
          noRowsOverlay: () => (
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              height='100%'
            >
              <Chip label='No shipment history found' color='default' />
            </Box>
          ),
        }}
      />
    </Box>
  );
};

export default ShipmentHistoryTable;
